import { ApplicationRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { IMsfFormBuilder } from '@components/form-builder/form-builder';
import { FilterOperator } from '@app/utils/filter-operator';
import {
  Bid,
  CustomFieldViewModel,
  FieldService,
  SeasonService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { BidService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import { BidWorkTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/bidWorkType.service';
import { Crop, CropService, FieldFilter } from '@6grain/masofa-monolith-api-client/dictionaries';
import { CreateBidFormComponent } from '@app/pages/electronic-field-map/pages/fields/passport/forms/create-bid-form/create-bid-form.component';
import { BidFormData } from '@app/pages/electronic-field-map/pages/fields/passport/forms/create-bid-form/bid-form-data';
import {
  buildBidWorkTypeFormField,
  BID_WORK_TYPE_IDS,
} from '@shared/data/bid-work-type.util';
import { ServerTranslateService } from '@services/server-translate.service';
import {
  buildSeasonCreateBidFormFields,
  loadSeasonSelectOptions,
  SeasonSelectOption,
} from '@shared/data/bid-create-from-season.util';
import { BidsToSeasonService } from '@services/bids-to-season.service';

export interface CropIdentificationCreateRequestContext {
  fieldId: string;
  cropId?: string | null;
  preferredCrop?: Crop | null;
  lat?: number | null;
  lng?: number | null;
  description?: string | null;
}

interface OpenCropIdentificationCreateRequestDeps {
  dialogService: DialogService;
  bidService: BidService;
  bidWorkTypeService: BidWorkTypeService;
  fieldService: FieldService;
  seasonService: SeasonService;
  cropService: CropService;
  serverTranslate: ServerTranslateService;
  datePipe: DatePipe;
  translate: TranslateService;
  appRef: ApplicationRef;
  bidsToSeason: BidsToSeasonService;
}

export function openCropIdentificationCreateRequestDialog(
  context: CropIdentificationCreateRequestContext,
  deps: OpenCropIdentificationCreateRequestDeps,
  onSuccess?: () => void,
): DynamicDialogRef | undefined {
  const fieldId = context.fieldId?.trim();
  if (!fieldId) {
    return undefined;
  }

  const bid: BidFormData = {
    fieldId,
    cropId: (context.cropId ?? null) as any,
    deadlineDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    description: context.description ?? deps.translate.instant('CROP_IDENTIFICATION'),
    lat: (context.lat ?? null) as any,
    lng: (context.lng ?? null) as any,
    bidWorkType: BID_WORK_TYPE_IDS.Default,
  };

  let dialogRef: DynamicDialogRef | undefined;

  deps.bidsToSeason
    .isEnabled()
    .pipe(
      take(1),
      switchMap((useSeasonCreate) => {
        if (useSeasonCreate) {
          const seasonHolder = { list: [] as SeasonSelectOption[] };
          const fields = buildSeasonCreateBidFormFields({
            getSeasonOptions: () => seasonHolder.list,
            bidWorkTypeService: deps.bidWorkTypeService,
          });
          dialogRef = openDialog(bid, fields, true, deps, onSuccess);
          return loadSeasonsForField(fieldId, deps.seasonService, deps.cropService, deps.serverTranslate).pipe(
            catchError(() => of([] as SeasonSelectOption[])),
            map((seasons) => {
              seasonHolder.list = seasons;
            }),
          );
        }

        const cropHolder = { list: [] as Crop[] };
        const fields = getCreateBidFormFields(cropHolder, deps.bidWorkTypeService);
        dialogRef = openDialog(bid, fields, false, deps, onSuccess);
        return loadNotHarvestingCropsForField(
          fieldId,
          deps.fieldService,
          deps.seasonService,
          deps.datePipe,
        ).pipe(
          catchError(() => of([] as Crop[])),
          map((crops) => {
            cropHolder.list = withPreferredCrop(crops, context.preferredCrop);
          }),
        );
      }),
    )
    .subscribe(() => {
      deps.appRef.tick();
    });

  return dialogRef;
}

function openDialog(
  bid: BidFormData,
  fields: IMsfFormBuilder[],
  useSeasonCreate: boolean,
  deps: OpenCropIdentificationCreateRequestDeps,
  onSuccess?: () => void,
): DynamicDialogRef {
  const dialogRef = deps.dialogService.open(CreateBidFormComponent, {
      draggable: false,
      resizable: false,
    header: deps.translate.instant('Create request'),
    width: '40%',
    closable: true,
    modal: true,
    appendTo: 'body',
    duplicate: true,
    baseZIndex: 12000,
    data: {
      model: bid,
      useSeasonCreate,
      serviceMethod: useSeasonCreate
        ? 'customCreateFromSeason'
        : 'cropMonitoringBidCustomCreatePost',
      service: deps.bidService,
      fields,
    },
  });

  dialogRef?.onClose?.subscribe((result: { success?: boolean } | undefined) => {
    if (result?.success) {
      onSuccess?.();
    }
  });

  return dialogRef;
}

function getCreateBidFormFields(cropHolder: {list: Crop[]}, bidWorkTypeService: BidWorkTypeService): IMsfFormBuilder[] {
  return [
    {
      name: 'cropId',
      title: 'Crop',
      type: 'select',
      placeholder: 'Select crop',
      getSelectOptions: () => cropHolder.list,
    },
    {
      name: 'deadlineDate',
      title: 'Deadline',
      type: 'date',
      placeholder: 'Select date',
    },
    {
      name: 'customer',
      title: 'Customer',
      type: 'input',
    },
    {
      name: 'description',
      title: 'Description',
      type: 'textarea',
    },
    buildBidWorkTypeFormField(bidWorkTypeService),
  ];
}

function loadSeasonsForField(
  fieldId: string,
  seasonService: SeasonService,
  cropService: CropService,
  serverTranslate: ServerTranslateService,
): Observable<SeasonSelectOption[]> {
  return seasonService.cropMonitoringSeasonGetActiveSeasonByDateGet(fieldId).pipe(
    switchMap((seasons) =>
      loadSeasonSelectOptions(seasons ?? [], cropService, (crop) =>
        serverTranslate.getDisplayValue(crop, 'names') || '',
      ),
    ),
  );
}

function loadNotHarvestingCropsForField(
  fieldId: string,
  fieldService: FieldService,
  seasonService: SeasonService,
  datePipe: DatePipe,
): Observable<Crop[]> {
  const filters: FieldFilter[] = [
    {
      filterField: 'id',
      filterOperator: FilterOperator.Equals,
      filterValue: fieldId,
    },
  ];

  return fieldService.cropMonitoringFieldCustomGetByQueryPost({ filters }).pipe(
    take(1),
    switchMap((fields) => {
      if (!fields?.length) {
        return of([] as Crop[]);
      }
      const customField = fields[0] as CustomFieldViewModel;
      const currentDate = datePipe.transform(new Date(), 'yyyy-MM-dd') as string;
      return seasonService.cropMonitoringSeasonGetByQueryPost({
        filters: [
          {
            filterField: 'fieldId',
            filterOperator: FilterOperator.Equals,
            filterValue: fieldId,
          },
          {
            filterField: 'PlantingDate',
            filterOperator: FilterOperator.LessThanOrEqual,
            filterValue: currentDate,
          },
        ],
      }).pipe(
        take(1),
        map((seasons) => {
          const cropIds = seasons?.map((s) => s.cropId) ?? [];
          const out: Crop[] = [];
          (customField?.crops as Crop[])?.forEach((crop) => {
            if (cropIds.includes(crop.id)) {
              out.push(crop);
            }
          });
          return out;
        }),
      );
    }),
  );
}

function withPreferredCrop(crops: Crop[], preferredCrop?: Crop | null): Crop[] {
  if (!preferredCrop?.id) {
    return [...crops];
  }

  if (crops.some((crop) => crop.id === preferredCrop.id)) {
    return [...crops];
  }

  return [preferredCrop, ...crops];
}
